import React, { FC } from 'react'
import Loading from '../common/Loading'

import { ApplicationWorkTime } from '../../../states/types/member'

import { format, combineYearMonthAndDay, diffDays, dateManipulateDay } from '../../../utils/date'
import { useTranslation } from 'react-i18next'
import TransAppName from './TransAppName'
import { useParams } from 'react-router-dom'
import { LangValue } from '../../../states/types/common'
import { convertHexColor } from '../../../utils/color'
import { uniqueId } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import InfoTips from '../common/InfoTips'

interface ApplicationWorkTimeProps {
  isLoading: boolean | undefined
  applicationWorkTime: ApplicationWorkTime[] | undefined
  date: string | {start_date: string, end_date: string}
  mode: 'daily' | 'monthly' | 'custom'
  setApplicationFilter: (applicationFilter: string) => void
}

const ApplicationWorkTime: FC<ApplicationWorkTimeProps> = (props) => {
  const { t } = useTranslation()
  const { language } = useParams<{ language: LangValue }>()

  const {
    isLoading,
    applicationWorkTime,
    date,
    mode,
    setApplicationFilter,
  } = props

  let y = 0
  const calcBarPosition = (index: number) => {
    if (!applicationWorkTime) return "0px"
    if (index !== 0) {
      if (applicationWorkTime[index - 1].time.past.time === 0) {
        y += 120
      } else {
        y += 200
      }
    }
    return `${y}px`
  }

  const calcHeight = () => {
    if (!applicationWorkTime) return "0px"
    return `${applicationWorkTime.length * 200 - applicationWorkTime.filter((item) => item.time.past.time === 0).length * 80}px`
  }

  const calcBarWidth = (value: number) => {
    const base = mode === "daily" ? 1 : 10
    return `${((value / 36000.0) * 100) / base}%`
  }

  const buildPositiveNegativeColor = (value: number) => (0 <= value ? "#0DB765" : "#DD4A38")

  const compareText = (type: string) => {
    if (type === "daily") {
      return (
        `(${t('parts.application_work_time.compare_past_30')})`
      )
    }
    if (type === "monthly") {
      return (
        `(${t('parts.application_work_time.compare_last_month')})`
      )
    }
    if (type === "custom") {
      return (
        `(${t('parts.application_work_time.compare_past')})`
      )
    }
    return ""
  }

  const presentBarText = (type: string) => {
    if (type === "daily") {
      return (
        `(${format(date, t('time.date.formats.with_day'), language)})`
      )
    }
    if (type === "monthly" && typeof date === 'string') {
      return (
        `(${combineYearMonthAndDay(date, 1, t('time.date.formats.only_month'), language)})`
      )
    }
    if (type === "custom" && typeof date !== 'string') {
      return (
        // eslint-disable-next-line max-len
        `(${format(date.start_date, t('time.date.formats.with_day'), language)} ~ ${format(date.end_date, t('time.date.formats.with_day'), language)})`
      )
    }
    return ""
  }

  const pastBarText = (type: string) => {
    if (type === "daily") {
      return (
        `(${t('parts.application_work_time.past_30_average')})`
      )
    }
    if (type === "monthly") {
      return (
        `(${t('parts.application_work_time.last_month')})`
      )
    }
    if (type === "custom" && typeof date !== 'string') {
      const diffCount = (diffDays(date.start_date, date.end_date) * -1) - 1
      const pastStartDate = format(dateManipulateDay(date.start_date, diffCount), t('time.date.formats.with_day'), language)
      const pastEndDate = format(dateManipulateDay(date.end_date, diffCount), t('time.date.formats.with_day'), language)
      return (
        // `(過去平均)`
        `(${pastStartDate} ~ ${pastEndDate})`
      )
    }
    return ""
  }

  return (
    <>
      <div style={{ marginLeft: "0.5em", marginRight: "0.5em" }}>
        <div className="box">
          <div style={{ paddingTop: "1em", paddingBottom: "1.5em", textAlign: "center" }}>
            <span style={{ fontSize: "24px", color: "#094E8D", fontWeight: "bold" }}>{t('parts.application_work_time.title')}</span>
          </div>
          <InfoTips>
            <>
              <div>
                各アプリケーションの使用時間を表示しています
              </div>
            </>
          </InfoTips>
          {
            isLoading || !applicationWorkTime ? (
              <Loading />
            ) : (
              <>
                <div style={{ paddingBottom: "2em", paddingLeft: "2em", paddingRight: "2em" }}>
                  <div className="flex" style={{ justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
                    {
                        applicationWorkTime?.map((item) => (
                          <div key={uniqueId()} className="flex" style={{ justifyContent: "center", alignItems: "center", paddingRight: "0.5em" }}>
                            <div style={{ width: "1em", height: "1em", background: convertHexColor(item.biz.color, 0.8) }} />
                            <span style={{ paddingLeft: "0.25em", fontSize: "14px" }}><TransAppName appName={item.name} /></span>
                          </div>
                        ))
                      }
                  </div>
                </div>
                <div style={{ position: "relative", paddingBottom: "2em" }}>
                  <div style={{ paddingLeft: "2em", paddingRight: "2em" }}>
                    <div className="flex" style={{ border: "1px solid rgba(51, 51, 51, 0.4)", height: calcHeight() }}>
                      {
                          Array.from(Array(20).keys()).map((index) => {
                            const base = mode === "daily" ? 1 : 10
                            return (
                              <div key={index} style={{ width: "5%", border: "1px solid rgba(51, 51, 51, 0.04)" }}>
                                {
                                  index % 2 === 0 && (
                                    <span style={{ position: "absolute", top: calcHeight(), fontSize: "12px" }}>
                                      {(index / 2) * base}
                                      h
                                    </span>
                                  )
                                }
                                {
                                  index === 19 && (
                                    <span style={{ position: "absolute", top: calcHeight(), right: "10px", fontSize: "12px" }}>
                                      {10 * base}
                                      h
                                    </span>
                                  )
                                }
                              </div>
                            )
                          })
                        }
                    </div>
                  </div>
                  {
                      applicationWorkTime?.map((item, index) => (
                        <div
                          key={uniqueId()}
                          style={{
                            width: "100%",
                            paddingLeft: "2em",
                            paddingRight: "2em",
                            marginTop: "2em",
                            marginBottom: "0.5em",
                            position: "absolute",
                            top: calcBarPosition(index),
                            left: "0px",
                          }}
                        >
                          <div style={{ paddingLeft: "0.75em" }}>
                            <span style={{ fontSize: "20px" }}><TransAppName appName={item.name} /></span>
                          </div>
                          {
                              item.name !== 'no_operation' && (
                                <div style={{ paddingLeft: "0.75em", paddingTop: "0.25em" }}>
                                  {t('parts.application_work_time.production')}
                                  :
                                  &nbsp;
                                  <span style={{ fontWeight: "bold" }}>
                                    {item.biz.point.toLocaleString()}
                                    &nbsp;
                                    biz
                                  </span>
                                  {
                                    item.time.past.time !== 0 && (
                                      <>
                                        <span style={{ marginLeft: "0.25em", color: buildPositiveNegativeColor(item.biz.percent) }}>
                                          {
                                            item.biz.percent >= 0 && (<>+</>)
                                          }
                                          {item.biz.percent}
                                          ％
                                        </span>
                                        <span style={{ color: "#cccccc" }}>
                                          {compareText(mode)}
                                        </span>
                                      </>
                                    )
                                  }
                                </div>
                              )
                            }
                          <div className="flex" style={{ alignItems: "center" }}>
                            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                            <div
                              className="application-bar"
                              onClick={() => setApplicationFilter(item.name)}
                              style={{
                                marginTop: "0.5em",
                                height: "50px",
                                background: convertHexColor(item.biz.color, 0.8),
                                width: calcBarWidth(item.time.now.time),
                              }}
                            />
                            <div
                              style={{
                                textAlign: "center",
                                position: "absolute",
                                left: "30px",
                                color: "#FFFFFF",
                                textShadow:
                                  // eslint-disable-next-line max-len
                                  "1px 1px 1px #666, -1px -1px 1px #666, -1px 1px 1px #666, 1px -1px 1px #666, 0 1px 1px #666, 0 -1px 1px #666, -1px 0 1px #666, 0 -1px 1px #666",
                              }}
                            >
                              <div>
                                <span style={{ fontSize: "20px", fontWeight: "bold" }}>{item.time.now.hour}</span>
                                &nbsp;
                                {t('parts.application_work_time.hour')}
                                <span style={{ fontSize: "20px", fontWeight: "bold" }}>
                                  &nbsp;
                                  {item.time.now.minute}
                                </span>
                                &nbsp;
                                {t('parts.application_work_time.minute')}
                              </div>
                              <div>
                                {presentBarText(mode)}
                              </div>
                            </div>
                          </div>
                          {
                              item.time.past.time !== 0 && (
                                <div className="flex" style={{ alignItems: "center" }}>
                                  <div
                                    className="application-bar"
                                    data-name={item.name}
                                    style={{
                                      marginTop: "0.5em",
                                      height: "50px",
                                      background: "#C4C4C4",
                                      width: calcBarWidth(item.time.past.time),
                                    }}
                                  />
                                  <div
                                    style={{
                                      textAlign: "center",
                                      position: "absolute",
                                      left: "30px",
                                      color: "#FFFFFF",
                                      textShadow:
                                        // eslint-disable-next-line max-len
                                        "1px 1px 1px #666, -1px -1px 1px #666, -1px 1px 1px #666, 1px -1px 1px #666, 0 1px 1px #666, 0 -1px 1px #666, -1px 0 1px #666, 0 -1px 1px #666",
                                    }}
                                  >
                                    <div>
                                      <span style={{ fontSize: "20px", fontWeight: "bold" }}>{item.time.past.hour}</span>
                                      &nbsp;
                                      {t('parts.application_work_time.hour')}
                                      <span style={{ fontSize: "20px", fontWeight: "bold" }}>
                                        &nbsp;
                                        {item.time.past.minute}
                                      </span>
                                      &nbsp;
                                      {t('parts.application_work_time.minute')}
                                    </div>
                                    <div>
                                      {pastBarText(mode)}
                                    </div>
                                  </div>
                                </div>
                              )
                            }
                        </div>
                      ))
                    }
                </div>
              </>
            )
          }
        </div>
      </div>
    </>
  )
}

export default ApplicationWorkTime
